import { FC, useEffect, useRef, useState } from "react";

import { useForm } from "react-hook-form";

import { useCurrentUserContext } from "../../../../../../common/contexts/CurrentUserContext";
import { trpc } from "../../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "../../useFormSaveAndNavigate";
import { getTicketingDescriptionStepWording, prefillDescription } from "../shared/helper";

import { LiveEditorStepDescriptionPoweredByAIStateless } from "./LiveEditorStepDescriptionPoweredByAI.stateless";
import { useDescriptionGenerationPolling } from "./useDescriptionGenerationPolling";

import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";
import { useArrow } from "@/components/LiveEditorElements/AnimatePreview/Arrow/ArrowContext";
import { StepDescriptionFormData } from "@/features/LiveFormEditor/Steps/StepDescription/shared/types";

export const TicketingStepDescriptionPoweredByAI: FC = () => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { apiFormObject: apiTicketing, isCreatedFromDuplicate } = useLiveFormEditorContext<"ticketing">();
  const formCategory = apiTicketing.formCategory;
  const { refreshArrowPosition, hideArrow, showArrow } = useArrow();
  const prefilledText = prefillDescription(t, organization?.name, apiTicketing.formCategory);

  const localizedTicketingField = apiTicketing.ticketingFields?.find((field) => field.locale === apiTicketing.locale);
  const initialDescription = localizedTicketingField?.description || null;

  const defaultValues: StepDescriptionFormData = {
    description: initialDescription,
  };
  const form = useForm<StepDescriptionFormData>({ defaultValues });

  const [isGenerating, setIsGenerating] = useState(false);
  const [aiGeneratedDescription, setAiGeneratedDescription] = useState<string | null>(null);

  const { pollDescriptionGeneration } = useDescriptionGenerationPolling(apiTicketing.id);

  useEffect(() => {
    if (initialDescription) {
      return;
    }

    let mounted = true;

    const fetchDescription = async () => {
      setIsGenerating(true);
      const { descriptionStatus, description } = await pollDescriptionGeneration();

      if (!mounted) {
        return;
      }

      if (descriptionStatus === "completed" && description) {
        setAiGeneratedDescription(description);
        form.setValue("description", description, { shouldDirty: true });
      } else if (descriptionStatus === "failed") {
        // Fallback: keep the initial description (null) which shows the prefilled placeholder
        // The prefilledText is already available and will be used in the preview
        setAiGeneratedDescription(null);
      }

      setIsGenerating(false);
    };

    fetchDescription();

    return () => {
      mounted = false;
    };
  }, [apiTicketing.id, form, initialDescription, pollDescriptionGeneration]);

  const updateTicketingDescription = trpc.updateTicketingDescription.useMutation();
  const startThankYouEmailGenerationWithAIMutation = trpc.startThankYouEmailGenerationWithAI.useMutation();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepDescriptionFormData>({
    type: "ticketing",
    form,
    alwaysSaveOnNext: true,
    saveData: async (formData) => {
      return await updateTicketingDescription.mutateAsync({
        ticketingId: apiTicketing.id,
        description: formData.description,
      });
    },
    onFirstNextCompleted: () => {
      if (isCreatedFromDuplicate) {
        return;
      }
      startThankYouEmailGenerationWithAIMutation.mutateAsync({
        formId: apiTicketing.id,
        title: localizedTicketingField?.title || "",
        description: form.getValues("description"),
        campaignDate: null,
        campaignLocation: apiTicketing.address ?? null,
        formCategory,
        locale: apiTicketing.locale,
      });
    },
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const descriptionInPreview = formData.description || prefilledText;
      liveDataToMutate.ticketing.ticketingFields![0].description = descriptionInPreview;
      liveDataToMutate.ticketing.ticketingFields![0].sanitizedDescription = descriptionInPreview;
      liveDataToMutate.placeholders.isDescription = !formData.description;
    },
  });

  const description = form.watch("description");
  // When the description is toggled from null <-> non-null, hide the arrow and show it after a delay to avoid arrow flickering
  // This is needed because there is a lot of update of the DOM when the description is toggled
  const prevDescriptionRef = useRef(description);
  useEffect(() => {
    const wasNull = prevDescriptionRef.current === null;
    const isNull = description === null;

    // If toggled from null->defined or defined->null
    if (wasNull !== isNull) {
      hideArrow();
      setTimeout(() => {
        refreshArrowPosition();
        showArrow();
      }, 800);
    }

    prevDescriptionRef.current = description;
  }, [description, hideArrow, refreshArrowPosition, showArrow]);

  return (
    <LiveEditorStepDescriptionPoweredByAIStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      initialDescription={initialDescription}
      prefilledText={prefilledText}
      isGenerating={isGenerating}
      aiGeneratedDescription={aiGeneratedDescription}
      forbiddenWordMessage={
        isSweepstakesCategory(formCategory)
          ? t("dashboard", "sweepstakes.validation.raffleWord.description")
          : undefined
      }
      wording={{
        stepTitle: getTicketingDescriptionStepWording(t, formCategory).stepTitle,
        stepDescription: getTicketingDescriptionStepWording(t, formCategory).stepDescription,
      }}
    />
  );
};
